import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { FiMenu, FiCalendar, FiChevronDown, FiLogOut } from 'react-icons/fi'
import { useStateContext } from '../contexts/ContextProvider'
import { findNavItem } from './navigation'

const today = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(new Date())

const Navbar = () => {
  const { activeMenu, setActiveMenu, setScreenSize, screenSize, adminUser, logout } = useStateContext()
  const { pathname } = useLocation()
  const current = findNavItem(pathname)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  // Close the profile menu on outside click or Escape
  useEffect(() => {
    if (!menuOpen) return
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    const handleKey = (e) => e.key === 'Escape' && setMenuOpen(false)
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [menuOpen])

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth)
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setActiveMenu(screenSize > 900)
  }, [screenSize])

  const name = adminUser?.name || 'Admin'

  return (
    <header className="flex items-center justify-between gap-3 bg-white rounded-full shadow-card pl-3 pr-2 py-2">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={() => setActiveMenu(!activeMenu)}
          className="icon-btn shrink-0"
          aria-label="Toggle menu"
        >
          <FiMenu size={18} />
        </button>
        {current && (
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-gray-400 leading-none">{current.group}</p>
            <p className="text-sm font-semibold text-gray-900 truncate mt-1">{current.label}</p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="hidden md:inline-flex items-center gap-2 text-sm text-gray-600 bg-gray-100 rounded-full px-4 py-2">
          <FiCalendar size={15} />
          {today}
        </span>
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="flex items-center gap-2.5 rounded-full pl-1 pr-2 sm:pr-3 py-1 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            {adminUser?.photo ? (
              <img src={adminUser.photo} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <span className="w-8 h-8 rounded-full bg-brand-600 text-white text-sm font-semibold flex items-center justify-center">
                {name.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="hidden sm:block text-sm font-medium text-gray-900 max-w-[140px] truncate">{name}</span>
            <FiChevronDown size={15} className={`text-gray-500 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
          </button>
          {menuOpen && (
            <div role="menu" className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-card border border-gray-100 p-1.5 z-50">
              <button
                type="button"
                role="menuitem"
                onClick={logout}
                className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <FiLogOut size={16} />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Navbar
